import React from "react";
import isString from "lodash/isString";
import ButtonWithTooltip from "./components/buttonWithTooltip";
import { ButtonComponentType } from "./types";
import Variant from "shared/enums/Variant";
import { getClassNames } from "./utils";
import {
  __AddButton__,
  __CircleIconButton__,
  __CloseButton__,
  __CreateButton__,
  __CustomButton__,
  __DeleteButton__,
  __EditButton__,
  __LinkButton__,
  __SaveButton__,
  __SettingButton__,
  __PreviewButton__
} from "./components/flavours";
import ButtonType from "shared/enums/ButtonTypes";

/**
 * @todo: Once finalized remove existing button component and use this everywhere.
 */
export function BaseButtonComponent({
  variant = Variant.PRIMARY,
  type = ButtonType.BUTTON,
  ...props
}: ButtonComponentType) {
  const computedClassNames = React.useMemo(
    () =>
      getClassNames({
        variant: variant,
        className: props.className,
        disabled: props.disabled,
        iconOnly: !!props.icon && !props.label,
        isOutline: props.isOutline,
        hasShadow: props.hasShadow,
        isButtonWithIconText: Boolean(props.icon && props.label)
      }),
    [props.className, props.disabled, props.hasShadow, props.icon, props.isOutline, props.label, variant]
  );

  const title = React.useMemo(() => {
    if (props.tooltipContent) {
      return;
    }
    if (isString(props.title)) {
      return props.title;
    }
    if (isString(props.label)) {
      return props.label;
    }
  }, [props.label, props.title, props.tooltipContent]);

  if (!props.label && !props.icon) {
    /**
     * Throw error if label and icon both are not passed.
     * Typescript will throw error, but incase someone tries to use icon={null} as its expected type is ReactNode,
     * in that case this check is helpful.
     *  */
    throw new Error("Either label or icon must be provided");
  }

  return (
    <button
      id={props.id}
      data-testid={props.testId}
      type={type}
      title={title}
      ref={props.setTriggerRef}
      onClick={props.onClick}
      disabled={props.disabled}
      className={computedClassNames}>
      {!props.iconPositionRight && props.icon && <small className="ptButton_icon">{props.icon}</small>}
      {props.label}
      {props.iconPositionRight && props.icon && <small className="ptButton_icon">{props.icon}</small>}
    </button>
  );
}

/**
 * You must provide at least one of the following props:
 * @param label
 * @param icon
 */
function Button(props: ButtonComponentType) {
  if (props.tooltipContent) {
    return <ButtonWithTooltip {...props} />;
  }
  return <BaseButtonComponent {...props} />;
}

export default Button;

Button.Create = __CreateButton__;
Button.Delete = __DeleteButton__;
Button.Edit = __EditButton__;
Button.Save = __SaveButton__;
Button.Add = __AddButton__;
Button.Setting = __SettingButton__;
Button.CircleIcon = __CircleIconButton__;
Button.Link = __LinkButton__;
Button.Custom = __CustomButton__;
Button.Close = __CloseButton__;
Button.Preview = __PreviewButton__;
